package org.lennycodes.hldi.pages.admin

import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.css.px
import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.dom.*
import org.lennycodes.hldi.backend.*
import org.lennycodes.hldi.data.ResourceDocument
import org.w3c.files.File

@Composable
fun EditResourceDialog(
    resource: ResourceDocument,
    onDismiss: () -> Unit,
    onSuccess: () -> Unit
) {

    val scope = rememberCoroutineScope()

    var title by remember { mutableStateOf(resource.title) }
    var category by remember { mutableStateOf(resource.category) }
    var description by remember { mutableStateOf(resource.description) }
    var publishedOn by remember { mutableStateOf(resource.publishedOn) }
    var selectedFile by remember { mutableStateOf<File?>(null) }

    var isLoading by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf("") }

    Div(
        attrs = Modifier
            .padding(16.px)
            .then(DialogBackdropStyle.toModifier())
            .toAttrs {
                onClick {
                    if (!isLoading) {
                        onDismiss()
                    }
                }
            }
    ) {

        Div(
            attrs = Modifier
                .padding(32.px)
                .then(DialogCardStyle.toModifier())
                .toAttrs {
                    onClick {
                        it.stopPropagation()
                    }
                }
        ) {

            H3(attrs = DialogTitleStyle.toAttrs()) {
                Text("Update Resource")
            }

            // Title
            Div(attrs = FieldGroupStyle.toAttrs()) {

                Label(attrs = FieldLabelStyle.toAttrs()) {
                    Text("Title")
                }

                Input(
                    type = InputType.Text,
                    attrs = Modifier
                        .padding(11.px, 14.px)
                        .then(FieldInputStyle.toModifier())
                        .toAttrs {
                            value(title)
                            onInput { title = it.value }
                            if (isLoading) disabled()
                        }
                )
            }

            // Category
            Div(attrs = FieldGroupStyle.toAttrs()) {

                Label(attrs = FieldLabelStyle.toAttrs()) {
                    Text("Category")
                }

                Select(
                    attrs = Modifier
                        .padding(11.px, 14.px)
                        .then(FieldInputStyle.toModifier())
                        .toAttrs {
                            onChange { category = it.value.toString() }
                            if (isLoading) disabled()
                        }
                ) {
                    resourceCategoryList.forEach { categoryOption ->
                        Option(
                            value = categoryOption,
                            attrs = {
                                if (category == categoryOption) {
                                    selected()
                                }
                            }
                        ) {
                            Text(categoryOption)
                        }
                    }
                }
            }

            // Description
            Div(attrs = FieldGroupStyle.toAttrs()) {

                Label(attrs = FieldLabelStyle.toAttrs()) {
                    Text("Description")
                }

                TextArea(
                    value = description,
                    attrs = Modifier
                        .padding(11.px, 14.px)
                        .then(FieldInputStyle.toModifier())
                        .toAttrs {
                            value(description)
                            onInput { description = it.value }
                            if (isLoading) disabled()
                        }
                )
            }

            // Published on
            Div(attrs = FieldGroupStyle.toAttrs()) {

                Label(attrs = FieldLabelStyle.toAttrs()) {
                    Text("Published On")
                }

                Input(
                    type = InputType.Date,
                    attrs = Modifier
                        .padding(11.px, 14.px)
                        .then(FieldInputStyle.toModifier())
                        .toAttrs {
                            value(publishedOn)
                            onInput { publishedOn = it.value }
                            if (isLoading) disabled()
                        }
                )
            }

            // Replace document
            Div(attrs = FieldGroupStyle.toAttrs()) {

                Label(attrs = FieldLabelStyle.toAttrs()) {
                    Text("Replace Document (optional)")
                }

                Input(
                    type = InputType.File,
                    attrs = Modifier.toAttrs {
                        attr("accept", ".pdf,application/pdf")

                        onChange {
                            val input = it.target
                            selectedFile = input.files?.item(0)
                        }

                        if (isLoading) disabled()
                    }
                )

                if (selectedFile != null) {
                    P(
                        attrs = Modifier
                            .fontSize(13.px)
                            .toAttrs()
                    ) {
                        Text(selectedFile!!.name)
                    }
                }
            }

            if (errorMessage.isNotEmpty()) {
                P(
                    attrs = Modifier
                        .color(Color.rgb(220, 53, 69))
                        .toAttrs()
                ) {
                    Text(errorMessage)
                }
            }

            Row(
                horizontalArrangement = Arrangement.spacedBy(12.px)
            ) {

                Button(
                    enabled = !isLoading,
                    onClick = { onDismiss() }
                ) {
                    Text("Cancel")
                }

                Button(
                    enabled = !isLoading,
                    onClick = {

                        scope.launch {

                            try {

                                errorMessage = ""

                                if (title.isBlank()) {
                                    errorMessage = "Title required"
                                    return@launch
                                }

                                if (category.isBlank()) {
                                    errorMessage = "Category required"
                                    return@launch
                                }

                                if (description.isBlank()) {
                                    errorMessage = "Description required"
                                    return@launch
                                }

                                if (publishedOn.isBlank()) {
                                    errorMessage = "Published date required"
                                    return@launch
                                }

                                isLoading = true

                                var updatedUrl = resource.fileUrl
                                var updatedPath = resource.storagePath

                                if (selectedFile != null) {

                                    val uploadedFile = uploadResourceDocument(
                                        selectedFile!!,
                                        resource.id
                                    )

                                    updatedUrl = uploadedFile.url
                                    updatedPath = uploadedFile.storagePath

                                    // Delete old storage file now that the new one is uploaded
                                    if (resource.storagePath.isNotBlank() && resource.storagePath != updatedPath) {

                                        try {

                                            val oldRef = ref(
                                                FirebaseService.storage,
                                                resource.storagePath
                                            )

                                            deleteObject(oldRef).await()

                                        } catch (e: Throwable) {

                                            console.error("Failed to delete old document: ${e.message}")
                                        }
                                    }
                                }

                                val updatedResource = ResourceDocument(
                                    id = resource.id,
                                    title = title,
                                    category = category,
                                    description = description,
                                    fileUrl = updatedUrl,
                                    storagePath = updatedPath,
                                    publishedOn = publishedOn
                                )

                                setDoc(
                                    doc(
                                        collection(
                                            FirebaseService.db,
                                            "resources"
                                        ),
                                        resource.id
                                    ),
                                    updatedResource.toFirebaseData()
                                ).await()

                                onSuccess()
                                onDismiss()

                            } catch (e: Throwable) {

                                errorMessage = e.message ?: "Update failed"

                            } finally {

                                isLoading = false
                            }
                        }
                    }
                ) {
                    Text(if (isLoading) "Updating..." else "Update")
                }
            }
        }
    }
}